import React, { useState, useCallback, CSSProperties, useRef, useEffect } from 'react';
import { FilterIcon } from './icons';

export interface Column<T> {
  title: string;
  dataIndex: string;
  filterType?: 'text' | 'number' | 'date' | 'time' | 'multiSelect';
  expectedValue?: (value: T[keyof T]) => any;
  render?: (value: keyof T, record: T, index: number) => React.ReactNode;
  renderTitle?: (value: string, col: Column<T>, index: number) => React.ReactNode;
  filterable?: boolean;
  sortable?: boolean;
}

interface TableStyles {
  tableClassName?: string;
  headerClassName?: string;
  rowClassName?: string;
  cellClassName?: string;
  sortButtonClassName?: string;
  headerCellClassName?: string;
  headerCellStyle?: CSSProperties;
  tableStyle?: CSSProperties;
  headerStyle?: CSSProperties;
  rowStyle?: CSSProperties;
  cellStyle?: CSSProperties;
}

interface OptionsTableProps<T> extends TableStyles {
  data: T[];
  columns: Column<T>[];
  /**
   * When set, blur every rendered row at this index or later (Edge row-gating).
   * Applied to the actually-rendered `sortedData`, so it stays consistent with
   * the table's own client-side filter/sort rather than a server-side count.
   */
  blurRowsFrom?: number;
  /**
   * Optional gate overlay. Called with the count of rows actually rendered so
   * the caller can decide whether an upsell overlay is warranted (e.g. only
   * when more rows exist than are visible). Rendered inside a relative wrapper.
   */
  renderOverlay?: (renderedRowCount: number) => React.ReactNode;
}

type SortOrder = 'asc' | 'desc' | null;
type FilterState<T> = { col: Column<T>; key: string; operator: string; value: any } | null;

function OptionsTable<T>({
  blurRowsFrom,
  cellClassName,
  cellStyle,
  columns,
  data,
  headerCellClassName,
  headerCellStyle,
  headerClassName,
  headerStyle,
  renderOverlay,
  rowClassName,
  rowStyle,
  sortButtonClassName,
  tableClassName,
  tableStyle,
}: OptionsTableProps<T>) {
  const [sortConfig, setSortConfig] = useState<{ key: keyof T; order: SortOrder } | null>(null);
  const [filterState, setFilterState] = useState<FilterState<T>>(null);
  const [filteredData, setFilteredData] = useState<T[]>(data);
  const [sortedData, setSortedData] = useState<T[]>(filteredData);

  const handleApplyFilter = useCallback(
    (key: string, col: Column<T>) => (value: any, operator: string) => {
      setFilterState({ col, key, operator, value });
    },
    [],
  );
  const handleClearFilter = useCallback(() => {
    setFilterState(null);
    setFilteredData(data);
  }, [data]);

  const handleSort = useCallback((key: keyof T) => {
    setSortConfig(prev => {
      if (prev?.key === key) {
        const nextOrder = prev.order === 'asc' ? 'desc' : prev.order === 'desc' ? null : 'asc';
        return nextOrder ? { key, order: nextOrder } : null;
      }
      return { key, order: 'asc' };
    });
  }, []);

  useEffect(() => {
    if (!filterState) {
      setFilteredData(data);
      return;
    }
    const { col, key, operator, value } = filterState;

    const newFilteredData = data?.filter(item => {
      const itemValue = item[key] || '';

      const comparableValue = col?.expectedValue?.(itemValue) ?? itemValue;

      switch (operator) {
        case 'Equals':
          return comparableValue === value;
        case 'Not Contains':
          return !comparableValue.includes(value);
        case 'Before':
          return new Date(comparableValue) < new Date(value);
        case 'After':
          return new Date(comparableValue) > new Date(value);
        case 'Between':
          return new Date(comparableValue) >= new Date(value[0]) && new Date(comparableValue) <= new Date(value[1]);
        case 'Less Than':
          return comparableValue < value;
        case 'Greater Than':
          return comparableValue > value;
        case 'multi_select':
          return value.has(comparableValue);
        default:
          return true;
      }
    });

    setFilteredData(newFilteredData);
  }, [data, filterState]);

  useEffect(() => {
    if (!sortConfig) {
      setSortedData(filteredData);
      return;
    }
    const { key, order } = sortConfig;

    const newSortedData = [...filteredData].sort((a, b) => {
      const valA = a[key];
      const valB = b[key];

      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });

    setSortedData(newSortedData);
  }, [filteredData, sortConfig]);

  const getDistinctValues = useCallback((data: T[], col: Column<T>): any => {
    const values = data?.map(record => {
      if (col?.expectedValue) {
        return col?.expectedValue(record[col.dataIndex]);
      }
      return record[col.dataIndex];
    });

    return [...new Set(values)];
  }, []);

  const renderedRowCount = sortedData?.length ?? 0;
  const isRowBlurred = (rowIndex: number) => blurRowsFrom !== undefined && rowIndex >= blurRowsFrom;
  // Overlay decision is based on the rows actually rendered (post filter/sort),
  // so it never appears over a table whose visible rows are all un-gated.
  const overlay = renderOverlay ? renderOverlay(renderedRowCount) : null;

  const table = (
    <table className={tableClassName} style={tableStyle}>
      <thead>
        <tr className={headerClassName} style={headerStyle}>
          {columns?.map((col, colIndex) => (
            <th className={headerCellClassName} key={String(col.dataIndex) + colIndex} style={headerCellStyle}>
              <div className="flex items-center gap-1">
                {col.renderTitle ? col.renderTitle(col.title, col, colIndex) : <span>{col.title}</span>}
                <div>
                  {col.filterable && (
                    <Filter
                      data={col.filterType === 'multiSelect' ? getDistinctValues(data, col) : undefined}
                      onApply={handleApplyFilter(col.dataIndex as string, col)}
                      onClear={handleClearFilter}
                      type={col.filterType || 'text'}
                    />
                  )}
                </div>
                {col.sortable && (
                  <button className={sortButtonClassName} onClick={() => handleSort(col.dataIndex as keyof T)}>
                    {sortConfig?.key === col.dataIndex ? (sortConfig?.order === 'asc' ? '▲' : '▼') : '⇅'}
                  </button>
                )}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedData?.map((record, rowIndex) => (
          <tr
            className={`${rowClassName ?? ''}${isRowBlurred(rowIndex) ? ' blur-sm opacity-60 select-none' : ''}`}
            key={(record as any).id || rowIndex}
            style={rowStyle}
            aria-hidden={isRowBlurred(rowIndex) || undefined}
          >
            {columns?.map((col, colIndex) => (
              <td
                className={cellClassName}
                key={colIndex + String((record as any).id || rowIndex) + String(col.dataIndex)}
                style={cellStyle}
              >
                {col.render ? col.render(record[col.dataIndex], record, rowIndex) : record[col.dataIndex]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return overlay ? (
    <div className="relative">
      {table}
      {overlay}
    </div>
  ) : (
    table
  );
}

interface FilterProps<T> {
  type: 'text' | 'number' | 'date' | 'time' | 'multiSelect';
  data?: T;
  onApply: (value: any, operator: string) => void;
  onClear: () => void;
}

const operators = {
  date: ['Equals', 'Do Not Equal', 'Before', 'After', 'Between', 'Blank', 'Not Blank'],
  multiSelect: ['multi_select'],
  number: ['Equals', 'Do Not Equal', 'Less Than', 'Greater Than', 'Between', 'Blank', 'Not Blank'],
  text: ['Contains', 'Not Contains', 'Equals', 'Do Not Equal', 'Begins With', 'Ends With', 'Blank', 'Not Blank'],
  time: ['Equals', 'Before', 'After', 'Between', 'Blank', 'Not Blank'],
};

const excludeOperators = new Set(['multi_select']);

function Filter<T>({ data, onApply, onClear, type }: FilterProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState('');
  const [value2, setValue2] = useState('');
  const [operator, setOperator] = useState(operators[type][0]);
  const [selectedData, setSelectedData] = useState<(T | string)[]>([]);
  const filterRef = useRef<HTMLDivElement>(null);

  const toggle = useCallback(() => setIsOpen(prev => !prev), []);
  const close = useCallback(() => setIsOpen(false), []);

  const handleApply = () => {
    const appliedValue =
      operator === 'Between' ? [value, value2] : excludeOperators.has(operator) ? new Set(selectedData) : value;
    onApply(appliedValue, operator);
    close();
  };

  const handleCheckboxChange = (item: string) => {
    setSelectedData(prev => (prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]));
  };
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        close();
      }
    },
    [close],
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);

    return () => document.removeEventListener('click', handleClickOutside);
  }, [handleClickOutside]);

  const handleClear = () => {
    setValue('');
    setValue2('');
    setSelectedData([]);
    setOperator(operators[type][0]);
    onClear();
  };

  return (
    <div className="p-1 hover:cursor-pointer relative" ref={filterRef}>
      <div onClick={toggle}>
        <FilterIcon />
      </div>

      {isOpen && (
        <div className="absolute bg-white shadow-md right-0 top-6 p-3 min-w-[250px] border rounded-md z-50">
          {!excludeOperators.has(operator) && (
            <div className="mb-2">
              <select
                className="w-full p-1 border rounded"
                onChange={e => setOperator(e.target.value)}
                value={operator}
              >
                {operators?.[type]?.map(op => (
                  <option key={op} value={op}>
                    {op}
                  </option>
                ))}
              </select>
            </div>
          )}

          {type !== 'multiSelect' && (
            <div className="mb-2">
              {operator === 'Between' ? (
                <div className="flex gap-2">
                  <input
                    className="w-full p-1 border rounded"
                    onChange={e => setValue(e.target.value)}
                    type={type === 'date' ? 'date' : type === 'time' ? 'time' : 'text'}
                    value={value}
                  />
                  <input
                    className="w-full p-1 border rounded"
                    onChange={e => setValue2(e.target.value)}
                    type={type === 'date' ? 'date' : type === 'time' ? 'time' : 'text'}
                    value={value2}
                  />
                </div>
              ) : (
                <input
                  className="w-full p-1 border rounded"
                  onChange={e => setValue(e.target.value)}
                  placeholder={`Enter ${type} value`}
                  type={type === 'date' ? 'date' : type === 'time' ? 'time' : 'text'}
                  value={value}
                />
              )}
            </div>
          )}

          {type === 'multiSelect' && data && (
            <div className="max-h-40 overflow-y-auto">
              <input
                className="w-full p-1 border rounded mb-2"
                placeholder="Search..."
                type="text"
              />
              {Object.values(data)?.map((item, index) => (
                <label className="flex items-center gap-2" key={index}>
                  <input
                    checked={selectedData?.includes(item as any)}
                    onChange={() => handleCheckboxChange(item as any)}
                    type="checkbox"
                  />
                  <span>{String(item)}</span>
                </label>
              ))}
            </div>
          )}

          <div className="flex justify-between mt-2">
            <button
              className="text-blue-600 border border-blue-600 px-2 py-1 rounded hover:bg-blue-50"
              onClick={handleApply}
            >
              Apply
            </button>
            <button
              className="text-red-600 border border-red-600 px-2 py-1 rounded hover:bg-red-50"
              onClick={handleClear}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(OptionsTable) as typeof OptionsTable;
